/**
 * guestPrincipal.ts — is this patient id a checkout GUEST principal?
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: returning patients S7.1 (2026-09-25).
 *
 * WHY. A patient who pays without finishing their account is not a user in
 * identity. Their order is held by a GUEST principal: the gateway stamps
 * `checkoutGuestUserId(assessmentId)` from contracts on the public checkout
 * lanes, and orders keeps the case on it until a claim moves it to a real
 * account. That id is a UUID-shaped SHA-256 digest with two nibbles fixed,
 * the version `5` and the variant `a`:
 *
 *     xxxxxxxx-xxxx-5xxx-axxx-xxxxxxxxxxxx
 *
 * Identity mints every real user id with Prisma's `@default(uuid())`, which is
 * a random v4, so its version nibble is always `4`. A v5-shaped id therefore
 * never names an identity user. Staff screens use this to tell "no account
 * yet" apart from "an account whose record is missing" (the SCR panel, see
 * clinicians/scrPanelState.ts).
 *
 * WHY THE SHAPE AND NOT THE CONTRACTS HELPER. contracts' `isCheckoutGuestUserId`
 * is exact but needs Node `crypto` (its own docblock says the portal does not
 * import it), and it needs the assessment the principal was derived from. The
 * shape needs neither. It is also the right question for a staff screen: an
 * order still held by the guest principal of an EARLIER assessment (a
 * questionnaire redone after payment) is still not on an account.
 *
 * WHAT KEEPS IT HONEST. `guestPrincipal.spec.ts` builds real guest ids with
 * the contracts derivation and random v4 ids, and checks this predicate tells
 * them apart. If contracts ever changes the derivation's shape, that spec
 * fails instead of the SCR panel silently mislabelling patients.
 *
 * This is a DISPLAY rule. It grants nothing and is never sent anywhere: the
 * services decide ownership with the exact derivation.
 */

/** The contracts `checkoutGuestUserId` shape: version nibble 5, variant nibble a. */
const GUEST_PRINCIPAL_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-5[0-9a-f]{3}-a[0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * True when `userId` has the shape of a checkout guest principal: an order
 * paid by someone with no identity account yet. False for null/empty and for
 * every identity user id (random v4).
 */
export function isGuestShapedPrincipalId(userId: string | null | undefined): boolean {
  return typeof userId === 'string' && GUEST_PRINCIPAL_SHAPE.test(userId);
}
